'use client'

import { cn } from '@/util/cn'
import { FlashMessageContext } from '@/context/flash-message'
import { CreationToolFrame } from 'creation/src/creation-tools/types'
import CreationToolStep from 'creation/src/creation-tools/types/steps'
import { useRouter } from 'next/navigation'
import { ReactNode, useContext, useState } from 'react'
import { exactGradientClassNames } from './presentation/gradients'
import { useCreationStore } from './store/provider'
import { useCreationNextStep } from './use-creation-next-step'

type Props = {
    className?: string
    children?: ReactNode
    active?: boolean
    frames?: CreationToolFrame[]
    basePath: string
    preNavigateAction?: () => Promise<void>
}

/**
 * Ported from legacy components/continue-button.tsx. Legacy renders a
 * `GradientButton` as a `react-router` `Link`, and its click handler only
 * calls `preventDefault()` + `history.replace(nextUrl)` when a
 * `preNavigateAction` was given; without one the anchor navigates by its own
 * default, i.e. a **push** that leaves a history entry. There is no anchor
 * here, so navigation is always imperative — but the push/replace split is
 * reproduced exactly (spec §9.2 records the one thing that cannot be: a
 * `<button>` has no middle-click / open-in-new-tab).
 *
 * Background is `exactGradientClassNames.indigoPurple`
 * (presentation/gradients.ts), not the shared `gradientClassNames` — this
 * button's gradient is a measured value (task-13-report.md "Fix round 2"),
 * so it reproduces legacy's exact 4-stop, sRGB `linear-gradient(45deg, ...)`
 * rather than the simplified 2-stop/oklab wash other, un-measured gradient
 * washes on this screen use.
 */
const ContinueButton = ({ className, children, active = false, frames, basePath, preNavigateAction }: Props) => {
    const { showMessage } = useContext(FlashMessageContext)
    const steps = useCreationStore((state) => state.steps)
    const saveFrames = useCreationStore((state) => state.saveFrames)
    const [isWaiting, setIsWaiting] = useState(false)
    const router = useRouter()
    const nextStep = useCreationNextStep(steps)

    if (!nextStep) {
        return null
    }

    const nextUrl = `${basePath}${CreationToolStep.route(nextStep)}`

    const onClick = async () => {
        if (frames) {
            saveFrames(frames, nextStep)
        }

        if (!preNavigateAction) {
            // Legacy's unhandled click falls through to the `Link`, which pushes.
            router.push(nextUrl)
            return
        }

        setIsWaiting(true)
        try {
            await preNavigateAction()
            router.replace(nextUrl)
        } catch (err) {
            showMessage({ message: (err as Error)?.message || 'An error occurred', type: 'error' })
        } finally {
            setIsWaiting(false)
        }
    }

    return (
        // `className` lands on the wrapper, not the button — that is legacy's
        // shape (the styled `Container` takes it), and it is load-bearing: the
        // Create step's override neutralises this wrapper's sticky positioning
        // and bottom padding so its own button card can stick instead.
        <div className={cn('sticky bottom-0 w-full bg-giphyDarkestGrey pb-2.5', className)}>
            <button
                type="button"
                disabled={!active || isWaiting}
                onClick={onClick}
                className={cn(
                    'relative box-border flex h-[42px] w-full items-center justify-center rounded-[4px] px-2.5 text-center text-base font-bold text-giphyWhite active:opacity-80 disabled:pointer-events-none disabled:bg-giphyDarkGrey disabled:bg-none disabled:text-white/50',
                    exactGradientClassNames.indigoPurple
                )}
            >
                {children || `Continue to ${CreationToolStep.label(nextStep)}`}
            </button>
        </div>
    )
}

export default ContinueButton
